import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Minus, X, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

export default function Cart() {
  const { items, updateQty, removeItem, total } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-12 h-12 mx-auto text-white/20" />
        <h1 className="font-heading uppercase text-4xl mt-6">Your cart is empty</h1>
        <p className="font-sub text-white/50 mt-3">Time to gear up. Find your drop.</p>
        <Link to="/shop" className="btn-forged-red mt-8 inline-flex">
          Shop Collection <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12" data-testid="cart-page">
      <h1 className="font-heading uppercase text-5xl md:text-6xl">Cart</h1>
      <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF] mt-2">{items.length} ITEM(S)</div>

      <div className="grid lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2 space-y-3">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.size}`} className="brutal-border bg-[#0a0a0a] p-4 flex gap-4" data-testid={`cart-item-${i.product_id}`}>
              <img src={i.image} alt={i.name} className="w-24 h-24 object-cover border border-white/10" />
              <div className="flex-1">
                <div className="font-heading uppercase tracking-wide text-lg leading-tight">{i.name}</div>
                <div className="font-mono text-[10px] text-white/40 tracking-wider mt-1">SIZE {i.size}</div>
                <div className="font-heading text-xl mt-2" style={{ color: i.accent }}>${i.price.toFixed(2)}</div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeItem(i.product_id, i.size)} data-testid={`cart-remove-${i.product_id}`} className="text-white/40 hover:text-[#FF3333]">
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-center border border-white/15">
                  <button onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="w-8 h-8 hover:bg-white/5 text-white/70">
                    <Minus className="w-3 h-3 mx-auto" />
                  </button>
                  <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${i.product_id}`}>{i.quantity}</span>
                  <button onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="w-8 h-8 hover:bg-white/5 text-white/70">
                    <Plus className="w-3 h-3 mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="brutal-border bg-[#0a0a0a] p-6 h-fit">
          <div className="font-heading uppercase text-xl mb-4">Order</div>
          <div className="flex justify-between font-sub text-white/70">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-sub text-white/70 mt-2">
            <span>Shipping</span>
            <span className="text-[#00E5FF]">Calculated at checkout</span>
          </div>
          <div className="border-t border-white/10 mt-4 pt-4 flex justify-between font-heading text-2xl">
            <span>Total</span>
            <span data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <button onClick={checkout} disabled={loading} data-testid="cart-checkout" className="btn-forged-red w-full mt-6 disabled:opacity-50">
            {loading ? "Redirecting..." : "Checkout"} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="font-mono text-[9px] text-white/30 tracking-widest text-center mt-3">
            SECURE STRIPE CHECKOUT
          </div>
        </div>
      </div>
    </div>
  );
}
